import {
  Avatar as _Avatar,
  Flex as _Flex,
  Button as _Button,
  Center as _Center,
  Drawer as _Drawer,
  DrawerBody as _DrawerBody,
  DrawerContent as _DrawerContent,
  DrawerFooter as _DrawerFooter,
  DrawerHeader as _DrawerHeader,
  DrawerOverlay as _DrawerOverlay,
  Icon as _Icon,
  Link as _Link,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useShowToast } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useQuery } from '@tanstack/react-query';
import {
  useGetInterviewScorecardsQuery,
  useSubmitScoreCardMutation,
} from '@/store/query/ats-applications';
import { getAtsJobDetails } from '@/store/atsJobDetails';
import { AutoSaveLoadingIndicator } from '@/components/ats/collect-score-card/AutoSaveLoadingIndicator';
import { KText } from '@/ui-library/components/text';
import { ScorecardBody } from '@/components/ats/collect-score-card/ScorecardBody';
import { CenterSpinner } from '@/components/ats/loadders';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { ContactDetails } from '@/components/contacts/details';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { EmptyResults } from '@/components/ats/empty-results';
import NoTask from '@/assets/icons/ats/NoTask';
import { ScoreCardProps, SubmitScoreCardPayload, Scorecard } from './types';
import { ViewScorecardNavButtons } from '@/components/ats/collect-score-card/ViewScorecardNavButtons';
import { http } from '@/lib/ats-https';
import { LinkExternal02 } from '@kula-ai/icons/dual-tone/LinkExternal02';

// Chakra 2.10 + TypeScript 4.9 trip TS2590 ("union type too complex") when
// many style-prop-heavy components are used in a single JSX tree. Collapsing
// the components' types to React.ComponentType<any> kills the inference
// cascade. Runtime behavior is unchanged; Chakra style-prop IntelliSense is
// suppressed in this file only.
type CC = React.ComponentType<any>;
const Avatar = _Avatar as CC;
const Flex = _Flex as CC;
const Button = _Button as CC;
const Center = _Center as CC;
const Drawer = _Drawer as CC;
const DrawerBody = _DrawerBody as CC;
const DrawerContent = _DrawerContent as CC;
const DrawerFooter = _DrawerFooter as CC;
const DrawerHeader = _DrawerHeader as CC;
const DrawerOverlay = _DrawerOverlay as CC;
const Icon = _Icon as CC;
const Link = _Link as CC;

const CandidateLabel = ({ scorecard }: { scorecard: Scorecard }) => {
  const { candidate, ats_job_id, ats_application_id } = scorecard;
  const hasLink = Boolean(ats_job_id && ats_application_id);

  const avatar = (
    <Avatar
      className="avatar-md"
      w="24px"
      h="24px"
      borderRadius="xs"
      src={candidate?.avatar}
      name={candidate?.name}
    />
  );

  if (!hasLink) {
    return (
      <>
        {avatar}
        <KText size="md-medium" color="secondary-900">
          {candidate?.name}
        </KText>
      </>
    );
  }

  return (
    <Link
      href={`/jobs/${ats_job_id}?appId=${ats_application_id}&currentView=stage&tab=candidates`}
      display="flex"
      alignItems="center"
      gap="6px"
      _hover={{ textDecoration: 'none' }}
    >
      {avatar}
      <KText
        size="md-medium"
        color="primary-600"
        _hover={{ textDecoration: 'underline' }}
      >
        {candidate?.name}
      </KText>
      <Icon
        as={LinkExternal02}
        width="18px"
        height="18px"
        color="primary-600"
      />
    </Link>
  );
};

interface ScoreCardPanesProps {
  hasResume: boolean;
  resumeData: any;
  firstScorecard: Scorecard | undefined;
  acceptedFiles: string[];
  isScorecardClosed: boolean;
  data: Scorecard[] | undefined;
  formRef: React.MutableRefObject<any>;
  handleSubmit: (_values: any) => Promise<void>;
  onSuccess: () => void;
  showOnlyCurrUser: boolean;
  canFillForOthers: boolean;
  canSubmit: boolean;
  isJobUpdateAllowedStatus: boolean;
  expandOnly: string | null;
  viewSc: boolean;
  setIsEdit: (_v: boolean) => void;
  handleAutoSave: (..._args: any[]) => any;
}

const ScoreCardPanes = ({
  hasResume,
  resumeData,
  firstScorecard,
  acceptedFiles,
  isScorecardClosed,
  data,
  formRef,
  handleSubmit,
  onSuccess,
  showOnlyCurrUser,
  canFillForOthers,
  canSubmit,
  isJobUpdateAllowedStatus,
  expandOnly,
  viewSc,
  setIsEdit,
  handleAutoSave,
}: ScoreCardPanesProps) => (
  <Flex h="100%" overflow="hidden">
    <Flex
      w="50%"
      flexDir="column"
      borderRight="1px solid"
      borderColor="border-color"
      overflowY="auto"
      p="6px 6px"
    >
      {hasResume ? (
        <PDFReaderBrowser
          file={resumeData?.url}
          content_type={resumeData?.content_type}
          preview_url={resumeData?.preview?.url}
          fileName={firstScorecard?.candidate?.name}
          acceptedFiles={acceptedFiles}
          maxFiles={1}
        />
      ) : (
        <ContactDetails
          contact={firstScorecard?.candidate}
          wrapperStyles={{ p: '6px 6px' }}
        />
      )}
    </Flex>
    <Flex flex="1" flexDir="column" overflowY="auto" minH={0}>
      {!firstScorecard || isScorecardClosed ? (
        <EmptyResults
          icon={NoTask}
          wrapperProps={{ h: '100%', flex: 1 }}
          subText="SCORECARD UNAVAILABLE"
          subTextProps={{ color: 'secondary-600' }}
          text="This scorecard is no longer accepting responses"
          textProps={{ color: 'secondary-900' }}
          iconProps={{ h: '140px', w: '130px', mb: '0px' }}
        />
      ) : (
        <ScorecardBody
          scoreCardDetails={data}
          formRef={formRef}
          // @ts-expect-error - handleSubmit is defined in the ScorecardBody component
          handleSubmit={handleSubmit}
          onSuccess={onSuccess}
          showOnlyCurrUser={showOnlyCurrUser}
          canFillForOthers={canFillForOthers}
          canSubmit={canSubmit}
          isEdit={isJobUpdateAllowedStatus}
          expandOnly={expandOnly}
          viewSc={viewSc}
          setIsEdit={setIsEdit}
          skipHeader={true}
          handleAutoSave={handleAutoSave}
        />
      )}
    </Flex>
  </Flex>
);

export const ReviewScoreCard = ({
  isOpen = true,
  onClose = () => {},
  application = { id: '' },
  onSuccess = () => {},
  params: _params = {},
  showOnlyCurrUser = false,
  canFillForOthers = false,
  scoreCardID = null,
  expandOnly = null,
  reviewId = null,
  viewSc = false,
  onNext = () => {},
  onPrev = () => {},
  showNavButtons = false,
  isFirstScorecard = false,
  isLastScorecard = false,
}: ScoreCardProps) => {
  const formRef = useRef(null);
  const [isEdit, setIsEdit] = useState(false);
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const { currentApplication } = useSelector(getAtsJobDetails);

  const { data: resumeData } = useQuery({
    queryKey: ['resume', application?.id],
    enabled: !!application?.id,
    queryFn: async () => {
      try {
        const response = await http.get(
          `/ats_applications/${application?.id}/resume`
        );
        return response?.data;
      } catch (error) {
        console.error('Error fetching resume:', error);
        throw error;
      }
    },
  });

  const { data, isLoading } = useGetInterviewScorecardsQuery(
    {
      applicationId: application?.id,
      params: reviewId ? { ats_review_id: reviewId } : {},
    },
    { skip: !application?.id }
  );

  // When viewing a specific scorecard from the list, narrow data to that entry
  // so ScorecardBody doesn't render the full panel of all reviewer cards.
  const displayData =
    expandOnly && viewSc
      ? data?.filter((sc: Scorecard) => String(sc.id) === String(expandOnly))
      : data;

  // When expandOnly (viewScorecardId) is set, check if the target scorecard exists in the response
  const expandOnlyMissing =
    expandOnly &&
    data?.length &&
    !data?.some((sc: Scorecard) => String(expandOnly) === String(sc?.id));

  const firstScorecard: Scorecard | undefined = expandOnlyMissing
    ? undefined
    : scoreCardID
      ? data?.find((sc: Scorecard) => sc?.id === scoreCardID)
      : data?.[0];

  const scoreCardReviewerIds = data?.map((sc) => sc?.reviewer?.id);
  const canSubmit =
    canFillForOthers || scoreCardReviewerIds?.includes(currUser?.id);

  const applicationData = currentApplication?.data;
  const { ats_job } = applicationData || {};
  const isJobUpdateAllowedStatus = !['closed', 'archived'].includes(
    ats_job?.job_status
  );
  const hasResume = resumeData?.id;

  const currUserScorecard = data?.find(
    (item: Scorecard) => item?.reviewer?.id === currUser?.id
  );

  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: application?.id,
    scorecardId: showOnlyCurrUser ? currUserScorecard?.id : data?.[0]?.id,
    questions: showOnlyCurrUser
      ? currUserScorecard?.questions
      : data?.[0]?.questions,
  });

  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();

  const handleSubmit = async (values: any) => {
    const scorecard = showOnlyCurrUser
      ? (currUserScorecard ?? data?.[0])
      : data?.[0];

    if (!scorecard) return;

    const payload: SubmitScoreCardPayload = {
      rating: values?.rating,
      feedback: values?.feedback,
      questions:
        scorecard?.questions?.map((item, idx) => {
          const param: { question: string; answer?: string } = {
            question: item?.question,
          };
          if (values?.[idx + 1]) param.answer = values?.[idx + 1];
          return param;
        }) || [],
      status: 'submitted',
    };

    try {
      await submitScoreCard({
        applicationId: scorecard?.ats_application_id,
        scorecardId: scorecard?.id,
        payload,
      }).unwrap();

      toast({ title: 'Scorecard submitted successfully' });
      onSuccess();
      onClose();
    } catch {
      toast({ title: 'Scorecard submission failed', status: 'error' });
    }
  };

  const acceptedFiles = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const closeDrawer = () => {
    // onClose from useScorecardTaskModal handles both disclosure + URL cleanup
    onClose();
  };

  const showFullPageLoading = isLoading;
  const isScorecardClosed =
    !showFullPageLoading &&
    !!firstScorecard &&
    firstScorecard?.status === 'closed';

  const showFooter =
    firstScorecard &&
    !isScorecardClosed &&
    (((data?.length === 1 || showOnlyCurrUser) &&
      isJobUpdateAllowedStatus &&
      canSubmit &&
      !viewSc) ||
      (viewSc && isEdit));
  const drawerWidth = '90%';

  const handleFormSubmit = () => {
    if (formRef?.current) {
      formRef.current.submitForm();
      const errArray = Object.keys(formRef.current?.errors || {});
      if (errArray?.length > 0) {
        const element = document.querySelector(`label[for="${errArray[0]}"]`);
        element?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={closeDrawer}>
      <DrawerOverlay zIndex="1400" />
      <DrawerContent
        w={drawerWidth}
        maxW={drawerWidth}
        borderTopLeftRadius="xl"
      >
        {showNavButtons ? (
          <ViewScorecardNavButtons
            onClose={closeDrawer}
            onNext={onNext}
            onPrev={onPrev}
            isFirstScorecard={isFirstScorecard}
            isLastScorecard={isLastScorecard}
          />
        ) : (
          <Center
            w="48px"
            h="48px"
            left="-70px"
            top="32px"
            pos="absolute"
            bg="#fff"
            borderRadius="full"
            onClick={closeDrawer}
            cursor="pointer"
          >
            <Icon as={XIcon} w="20px" h="20px" />
          </Center>
        )}

        {!showFullPageLoading && firstScorecard && (
          <DrawerHeader
            p="24px 24px"
            borderBottom="1px solid"
            borderColor="border-color"
            display="flex"
            height="60px"
            alignItems="center"
            gap="6px"
          >
            <KText size="md-medium" color="secondary-900">
              Review for
            </KText>
            <CandidateLabel scorecard={firstScorecard} />
          </DrawerHeader>
        )}

        <DrawerBody overflowY="hidden" p="0">
          {showFullPageLoading ? (
            <CenterSpinner flex="1" />
          ) : (
            <ScoreCardPanes
              hasResume={Boolean(hasResume)}
              resumeData={resumeData}
              firstScorecard={firstScorecard}
              acceptedFiles={acceptedFiles}
              isScorecardClosed={isScorecardClosed}
              data={displayData ?? data}
              formRef={formRef}
              handleSubmit={handleSubmit}
              onSuccess={onSuccess}
              showOnlyCurrUser={showOnlyCurrUser}
              canFillForOthers={canFillForOthers}
              canSubmit={canSubmit}
              isJobUpdateAllowedStatus={isJobUpdateAllowedStatus}
              expandOnly={expandOnly}
              viewSc={viewSc}
              setIsEdit={setIsEdit}
              handleAutoSave={handleAutoSave}
            />
          )}
        </DrawerBody>

        {showFooter && (
          <DrawerFooter borderTop="1px solid" borderColor="border-color">
            <AutoSaveLoadingIndicator isSaved={isSaved} />
            <Button variant="secondary" mr={3} onClick={closeDrawer}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleFormSubmit}
              isLoading={isSubmitting}
              data-test-id="primary-cta-button"
            >
              Submit scorecard
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};
